import { useState, useEffect, useCallback } from 'react'
import { bookingAPI } from '../services/api/bookings'
import { useAsyncOperation } from './useAsyncOperation'
import { createLogger } from '../utils/logger'

const logger = createLogger('useVendorBookings')

/**
 * useVendorBookings - fetches and manages the booking queue for a vendor entity.
 * @param {string} entityId
 * @param {string} [statusFilter]
 * Returns { bookings, loading, error, updateStatus, refresh }
 */
export function useVendorBookings(entityId, statusFilter = '') {
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { execute } = useAsyncOperation()

  const load = useCallback(() => {
    if (!entityId) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    const params = statusFilter ? { status: statusFilter } : {}
    bookingAPI
      .getVendorBookings(entityId, params)
      .then((res) => {
        if (!cancelled) setBookings(res?.data?.bookings || [])
      })
      .catch((err) => {
        if (!cancelled) {
          logger.error('Failed to load vendor bookings', err)
          setError(err)
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [entityId, statusFilter])

  useEffect(() => load(), [load])

  const updateStatus = useCallback(async (groupId, status, reason, { onSuccess, onError } = {}) => {
    await execute(() => bookingAPI.updateGroupStatus(groupId, status, reason), {
      onSuccess: () => {
        setBookings((prev) =>
          prev.map((b) => b.group_id === groupId ? { ...b, status } : b)
        )
        onSuccess?.()
      },
      onError,
    })
  }, [execute])

  return { bookings, loading, error, updateStatus, refresh: load }
}
